import React, { useEffect, useRef, useCallback, useMemo, MutableRefObject, useState } from 'react';

import { useWindowSize } from 'usehooks-ts';
import { useDebouncedCallback } from 'use-debounce';
import { LngLatBounds, LngLat } from 'maplibre-gl'
import { Map, MapRef, Layer, Source, Point } from 'react-map-gl/maplibre';
import { BlockLines } from './BlockLines';
import { BlockPlaces } from '@ivyroot/block-places'
import { getPlace, getPendingSlapIdsForPlace } from '../places/Places'
import { useFetchSaveLatestStickerDesigns } from '../stickers/StickerDesigns'
import { useFetchSaveSlapsInBounds, writeSlapToBoard, deleteSlapFromBoardById, lookupExpandedSlap } from '../slaps/Slaps';
import { addAllObjectivesToMap } from '../objectives/DisplayObjectives'
import { renderVisiblePlaces, renderLayerForPlace } from '../places/DisplayPlaces';
import { deleteLayerForSlap, cleanupOrphanedSlapLayers, setSlapLayerVisibility } from '../slaps/DisplaySlaps'
import { renderLastFocusedPlace } from './DisplayLastFocusedPlace'
import { Board, DisplayedCenter, defaultDisplayedCenter, emptyDisplayedCenter, Place, SelectedItem, StickerDesign, Slap, SlapId, ExpandedSlap, PendingSlapList, PlaceId, FocusedPlace } from '../utils/Primitives'
import { checkExpandedSlapsForClick } from '../utils/LatLongTools'
import { sanitizeEnvUrl } from '../utils/shareLinks';

import 'maplibre-gl/dist/maplibre-gl.css';

type MapViewTarget = {
    center: [number, number];
    zoom?: number;
    bounds?: LngLatBounds | null;
};

type SlapMapProps = {
    clientDisplayedCenter: DisplayedCenter;
    setClientDisplayedCenter: (center: DisplayedCenter) => void;
    boardRef: MutableRefObject<Board>;
    focusedPlace: FocusedPlace;
    setFocusOnPlace: (focusedPlace: FocusedPlace) => void;
    handleSlapCreatorOpen: () => void;
    handleSlapExecutorOpen: () => void;
    latestSlap: Slap | null;
    realtimeLatestSlap: Slap | null;
    slapsInProgress: PendingSlapList | null;
    deletedSlaps: SlapId[];
    latestUpdateTime: number | null;
    onMapReady?: () => void;
    onSlapsFetched?: () => void;
    initialPlaceId?: PlaceId;
    fillContainer?: boolean;
    lockedView?: MapViewTarget | null;
    isLocked?: boolean;
}

export default function SlapMap(props: SlapMapProps) {
    const {
            clientDisplayedCenter,
            setClientDisplayedCenter,
            boardRef,
            latestSlap,
            realtimeLatestSlap,
            handleSlapCreatorOpen,
            handleSlapExecutorOpen,
            slapsInProgress,
            deletedSlaps,
            setFocusOnPlace,
            focusedPlace,
            latestUpdateTime,
            onMapReady,
            onSlapsFetched,
            fillContainer,
            lockedView,
            isLocked,
        } = props

    // TODO upgrade state to not have shadow refs
    const currActionButtonBounds = useRef<maplibregl.LngLatBounds | null>(null)
    const currSlapsInProgressList = useRef<ExpandedSlap[]>([])
    const currCenter = useRef<DisplayedCenter>(emptyDisplayedCenter)
    const focusedPlaceRef = useRef<FocusedPlace | null>(null)
    const mapRef = useRef<MapRef | null>(null);
    const pendingLockedView = useRef<MapViewTarget | null>(null);
    const blockLines = useMemo(() => BlockLines(), [])
    const { width, height } = useWindowSize();
    const calculatedMapHeight = useMemo(() => {
        if (fillContainer) {
            return '100%';
        }
        if (!width || !height) {
            return 300;
        }
        return (width > 1000) ? height * 0.94 : height * 0.52;
    }, [fillContainer, width, height]);
    const [showMapHeight, setShowMapHeight] = useState<string | number>(fillContainer ? '100%' : 300);
    const { isLoading : stickerDesignsLoaded, isFetched : stickerDesignsFetched } = useFetchSaveLatestStickerDesigns({boardRef, count: 15})
    const { isLoading : slapsLoading, isFetched : slapsFetched } = useFetchSaveSlapsInBounds({boardRef, displayInfo: clientDisplayedCenter})
    const isMapLocked = Boolean(isLocked);

    useEffect(() => {
        if (fillContainer) {
            setShowMapHeight('100%');
            return;
        }
        if (typeof calculatedMapHeight === 'number' && calculatedMapHeight > 0) {
            setShowMapHeight(calculatedMapHeight);
        }
    }, [calculatedMapHeight, fillContainer]);

    const debouncedCurrCenter = useDebouncedCallback(
        (value) => {
            setClientDisplayedCenter(value);
        },
        500
    )

    useEffect(() => {
        const doWrite = async () => {
        if (realtimeLatestSlap) {
            console.log(`Realtime slap received>>: ${realtimeLatestSlap.id}`);
            const pendingSlapIdsAtPlace = getPendingSlapIdsForPlace({
                boardRef,
                placeId: realtimeLatestSlap.place
            })
            if (mapRef.current) {
                for (const slapId of pendingSlapIdsAtPlace) {
                    deleteLayerForSlap({ mapRef, slapId })
                    deleteSlapFromBoardById({
                        boardRef,
                        placeId: realtimeLatestSlap.place,
                        slapId: slapId,
                    })
                }
            }
            await writeSlapToBoard({boardRef, slap: realtimeLatestSlap});
            renderLayerForPlace({boardRef, placeId: realtimeLatestSlap.place, mapRef})
        }
        }
        doWrite()
    }, [realtimeLatestSlap, boardRef, mapRef]);

    const mapFocusPlace = useCallback(() => {
        return {
            focusedPlace: focusedPlace,
        }
    }, [focusedPlace]);

    useEffect(() => {
        const actionButtonBounds = renderLastFocusedPlace({boardRef, mapRef, lastFocusedPlace: focusedPlace, slapsInProgress})
        if (actionButtonBounds) {
            currActionButtonBounds.current = new LngLatBounds(actionButtonBounds[3], actionButtonBounds[1])
        }
    }, [boardRef, mapRef, focusedPlace, slapsInProgress]);

    useEffect(() => {
        if (!lockedView) {
            pendingLockedView.current = null;
            return;
        }
        if (!mapRef.current) {
            pendingLockedView.current = lockedView;
            return;
        }
        pendingLockedView.current = null;
        if (lockedView.bounds) {
            mapRef.current.fitBounds(lockedView.bounds, { padding: 24, duration: 600 });
            return;
        }
        mapRef.current.flyTo({
            center: lockedView.center,
            zoom: lockedView.zoom ?? mapRef.current.getZoom(),
            duration: 600,
        });
    }, [lockedView]);

    useEffect(() => {
        currSlapsInProgressList.current = slapsInProgress ? slapsInProgress : []
     }, [slapsInProgress]);

    useEffect(() => {
        if (latestSlap && mapRef.current) {
            renderLayerForPlace({boardRef, mapRef: mapRef, placeId: latestSlap.place})
        }
    }, [boardRef, latestSlap])

    useEffect(() => {
        if (mapRef.current) {
            for (const slapId of deletedSlaps) {
                deleteLayerForSlap({mapRef, slapId})
            }
        }

    }, [deletedSlaps])

    const suppressCoveredSlaps = useCallback(() => {
        if (!mapRef.current) {
            return
        }
        const slapStore = boardRef.current.slapStore
        const placeStore = boardRef.current.placeStore
        const pendingDraftIds = new Set<SlapId>()
        Object.values(placeStore).forEach((place) => {
            if (!place || !place.pendingSlaps) {
                return
            }
            place.pendingSlaps.forEach((pendingId) => {
                if (pendingId !== undefined && pendingId !== null) {
                    pendingDraftIds.add(pendingId)
                }
            })
        })
        if (pendingDraftIds.size === 0) {
            Object.values(slapStore).forEach((slap) => {
                if (!slap) {
                    return
                }
                setSlapLayerVisibility({
                    mapRef,
                    slapId: slap.id,
                    visibility: 'visible',
                })
            })
            return
        }

        const coveredPlaceIds = new Set<PlaceId>()
        pendingDraftIds.forEach((draftId) => {
            const draftSlap = slapStore[draftId]
            if (!draftSlap || !draftSlap.isDraft) {
                return
            }
            const size = draftSlap.size ? draftSlap.size : 1
            const coveredPlaces = BlockPlaces.getPlaceIdsForSizedBlockPlace(
                draftSlap.place as number,
                size
            )
            if (!coveredPlaces) {
                return
            }
            coveredPlaces.forEach((placeId) => coveredPlaceIds.add(placeId))
        })

        Object.values(slapStore).forEach((slap) => {
            if (!slap || slap.isDraft) {
                return
            }
            const size = slap.size ? slap.size : 1
            const coveredPlaces = BlockPlaces.getPlaceIdsForSizedBlockPlace(
                slap.place as number,
                size
            )
            if (!coveredPlaces || coveredPlaces.length === 0) {
                return
            }
            const fullyCovered = coveredPlaces.every((placeId) =>
                coveredPlaceIds.has(placeId)
            )
            setSlapLayerVisibility({
                mapRef,
                slapId: slap.id,
                visibility: fullyCovered ? 'none' : 'visible',
            })
        })
    }, [boardRef, mapRef])

    useEffect(() => {
        if (mapRef.current && boardRef.current && clientDisplayedCenter.center) {
            renderVisiblePlaces({mapRef, boardRef})
            suppressCoveredSlaps()
            cleanupOrphanedSlapLayers({ mapRef, boardRef })
        }
    }, [mapRef, boardRef, clientDisplayedCenter, slapsFetched, latestUpdateTime, suppressCoveredSlaps]);

    useEffect(() => {
        if (slapsFetched) {
            onSlapsFetched?.();
        }
    }, [slapsFetched, onSlapsFetched]);

    // Add effect to handle focusedPlace changes
    useEffect((): void => {
        if (mapRef.current && focusedPlace?.placeId) {
            const southwestCorner = BlockPlaces.southwestCornerOfPlaceId(focusedPlace.placeId);
            if (southwestCorner) {
                // Add 0.005 to center in the middle of the block (since blocks are 0.01 x 0.01)
                const centerLng = southwestCorner.lng + 0.005;
                const centerLat = southwestCorner.lat + 0.005;

                // Check if the place is already visible in the current viewport
                const bounds = mapRef.current.getBounds();
                const isVisible = bounds.contains(new LngLat(centerLng, centerLat));

                // Move center if place isn't visible or if focus came from sidebar
                if (!isVisible || focusedPlace.source === 'sidebar' || focusedPlace.source === 'activity' || focusedPlace.source === 'url') {
                    console.log(`Setting map center to ${centerLng}, ${centerLat} from place ${focusedPlace.placeId}`);
                    mapRef.current.setCenter([centerLng, centerLat]);
                    // Only change zoom if the focus came from the sidebar or activity
                    if (focusedPlace.source === 'sidebar' || focusedPlace.source === 'activity' || focusedPlace.source === 'url') {
                        mapRef.current.setZoom(13);
                    }
                } else {
                    console.log(`Place ${focusedPlace.placeId} is already visible in viewport`);
                }
            }
        }
    }, [focusedPlace]);

    const onMapClicked = (lngLat: LngLat) => {
        const placeId = BlockPlaces.enclosingPlaceIdForPoint(lngLat)

        if (mapRef.current != null) {
            if (currActionButtonBounds.current) {
                if (currActionButtonBounds.current.contains(lngLat)) {
                    handleSlapCreatorOpen()
                    return
                }
            }
        }

        if (checkExpandedSlapsForClick(lngLat, currSlapsInProgressList.current) > 0) {
            handleSlapExecutorOpen()
            return
        }

        if (currSlapsInProgressList.current.length > 0) {
            return
        }

        const place = getPlace({boardRef, placeId})
        if (place === null) {
            return
        }
        if (place.bounds === null || !place.bounds) {
            return
        }
        let expandedSlap
        if (place.slap) {
            expandedSlap = lookupExpandedSlap({boardRef, slapId: place.slap})
        }
        const focusedPlace: FocusedPlace = {
            placeId: placeId,
            source: 'map'
        }
        setFocusOnPlace(focusedPlace)
        focusedPlaceRef.current = focusedPlace
        onMapLocationChanged({mapRef})
    }

    const onMapLocationChanged = (props:{
        mapRef: MutableRefObject<MapRef | null>,
    }) => {
        const { mapRef } = props
        if (mapRef.current) {
            const center = mapRef.current.getCenter()
            const zoom = mapRef.current.getZoom()
            const bounds = mapRef.current.getBounds()
            const placeId = BlockPlaces.enclosingPlaceIdForPoint(center)
            const newCenter: DisplayedCenter = {
                center,
                zoom,
                placeId,
                bounds,
            }
            currCenter.current = newCenter
            debouncedCurrCenter(newCenter)
        }
    }

    const initMapViewFromParams = (props:{
        mapRef: MutableRefObject<MapRef | null>,
    }) => {
        const { mapRef } = props
        const queryString = window.location.search.slice(1);
        const params = new URLSearchParams(queryString);
        const placeParam = params.get('place');
        const playerParam = params.get('player');

        if (placeParam && mapRef.current) {
            const placeId = parseInt(placeParam, 10);
            const place = getPlace({boardRef, placeId});

            if (place && place.bounds) {
                mapRef.current.fitBounds(place.bounds, { padding: 50 });
                const currZoom = mapRef.current.getZoom();
                mapRef.current.setZoom(currZoom - 1);
                const focusedPlace: FocusedPlace = {
                    placeId: placeId,
                    source: 'url'
                }
                setFocusOnPlace(focusedPlace);
                focusedPlaceRef.current = focusedPlace; // Set ref like click flow does

                // Force render the highlight after a brief delay to ensure map is ready
                setTimeout(() => {
                    const actionButtonBounds = renderLastFocusedPlace({boardRef, mapRef, lastFocusedPlace: focusedPlace, slapsInProgress})
                    if (actionButtonBounds) {
                        currActionButtonBounds.current = new LngLatBounds(actionButtonBounds[3], actionButtonBounds[1])
                    }
                }, 100)
            }
        } else if (playerParam) {
            // If there's a player param, we don't need to do anything with the map
            // The Game component will handle opening the player reviewer
            return;
        } else if (props && props.mapRef.current && focusedPlace?.placeId) {
            // If we have a focused place from server-side props, honor it
            const place = getPlace({ boardRef, placeId: focusedPlace.placeId });
            if (place && place.bounds) {
                props.mapRef.current.fitBounds(place.bounds, { padding: 50 });
                const currZoom = props.mapRef.current.getZoom();
                props.mapRef.current.setZoom(currZoom - 1);
            }
        }
    }


    const initializeGameObjects = () => {
        if (mapRef.current) {
            console.log(`Initializing map...`)
            currCenter.current.center = mapRef.current.getCenter()
            currCenter.current.zoom = mapRef.current.getZoom()
            mapRef.current.getCanvas().style.cursor = "default"
            console.log(`Adding all objectives to map...`)
            addAllObjectivesToMap({boardRef, mapRef})
            console.log(`Game active...`)
            renderVisiblePlaces({mapRef, boardRef})
            mapRef.current.on('moveend', () => {
                onMapLocationChanged({mapRef})
            })
            mapRef.current.on('click', (e) => {
                onMapClicked(e.lngLat)
            })
            onMapLocationChanged({mapRef})
            initMapViewFromParams({mapRef})
            if (onMapReady) {
                onMapReady();
            }
            if (pendingLockedView.current) {
                if (pendingLockedView.current.bounds) {
                    mapRef.current.fitBounds(pendingLockedView.current.bounds, { padding: 24, duration: 0 });
                } else {
                    mapRef.current.jumpTo({
                        center: pendingLockedView.current.center,
                        zoom: pendingLockedView.current.zoom ?? mapRef.current.getZoom(),
                    });
                }
                pendingLockedView.current = null;
            }
        }
    }

    const fallbackMapStyle = 'https://www.stickerchain.xyz/static/map-base-style-za8o2.json'
    const configuredMapStyle = sanitizeEnvUrl(process.env.NEXT_PUBLIC_MAP_BASE_STYLE)
    const mapStyle = configuredMapStyle &&
        (configuredMapStyle.startsWith('http://') ||
         configuredMapStyle.startsWith('https://') ||
         configuredMapStyle.startsWith('/'))
        ? configuredMapStyle
        : fallbackMapStyle

    const initialCenter = useMemo(() => {
        if (focusedPlace?.placeId) {
            const sw = BlockPlaces.southwestCornerOfPlaceId(
                focusedPlace.placeId
            )
            if (sw) {
                return new LngLat(sw.lng + 0.005, sw.lat + 0.005)
            }
        }
        return defaultDisplayedCenter.center
    }, [focusedPlace])

    const initialZoom = useMemo(() => {
        return focusedPlace?.placeId ? 13 : defaultDisplayedCenter.zoom
    }, [focusedPlace])

    return (
        <div className={fillContainer ? 'w-full h-full' : 'w-full'} style={fillContainer ? undefined : { height: showMapHeight }}>
            <Map
                ref={mapRef}
                id={'stkr-map'}
                onLoad={initializeGameObjects}
                onError={(event) => {
                    const error = event?.error ?? event
                    console.error('[SlapMap] MapLibre error', {
                        mapStyle,
                        error,
                    })
                }}
                initialViewState={{
                    longitude: initialCenter.lng,
                    latitude: initialCenter.lat,
                    zoom: initialZoom,
                }}
                dragPan={!isMapLocked}
                dragRotate={!isMapLocked}
                scrollZoom={!isMapLocked}
                doubleClickZoom={false}
                keyboard={!isMapLocked}
                boxZoom={!isMapLocked}
                touchPitch={!isMapLocked}
                touchZoomRotate={!isMapLocked}
                style={{ height: showMapHeight, width: '100%' }}
                mapStyle={mapStyle}
            >
                <Source type="geojson" data={blockLines}>
                    <Layer id='blockplace-grid' type={'line'} minzoom={9} paint={{'line-color': '#999999', 'line-width': 1, 'line-opacity': 0.55}} />
                </Source>
            </Map>
        </div>
    )

}
